import { useRef, useState } from 'react';
import {
    Alert,
    Button,
    Container,
    Grid,
    Stack,
    TextField,
    Typography,
    Box,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    ListItemIcon,
    useTheme,
    FormControlLabel,
    Switch,
    InputAdornment,
    Divider,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Avatar from '../info/Avatar';
import { RequestSnackbar, useRequest } from '../../api/Request';
import { useApi } from '../../api/Api';
import { useAuth } from '../../auth/Auth';
import {
    ArtistInfo,
    hasCreatedProfile,
    SubscriptionIntent,
    User,
    UserSummary,
} from '../../database/user';
import { useNavigate } from 'react-router-dom';
import { useCache } from '../../api/cache/Cache';
import { getConfig } from '../../config';
import DeleteIcon from '@mui/icons-material/Delete';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import SaveIcon from '@mui/icons-material/Save';
import {
    CurrencyExchange,
    NotificationsActive,
    Person,
    QuestionAnswer,
} from '@mui/icons-material';
import { PaymentModel } from '../../theSource/constants/PaymentModel';
import Icon from '../../components/Icon';
import { ResponseType } from '../../theSource/constants/ResponseType';
import NotificationSettingsEditor from './NotificationSettingsEditor';
import ArtistSubscriptionSettingsEditor from './ArtistSubscriptionSettingEditor';
import SubscriptionManager from './SubscriptionManager';

export interface ProfileCreatorFormProps {}

export enum ProfilePicAction {
    Update = 'Update',
    Delete = 'Delete',
}

const ProfileEditorPage: React.FC<ProfileCreatorFormProps> = () => {
    const api = useApi();
    const request = useRequest();
    const user = useAuth().user!;
    const navigate = useNavigate();
    const theme = useTheme();
    const [profilePicAction, setProfilePicAction] = useState<ProfilePicAction>();
    const [displayName, setDisplayName] = useState(user.displayName);
    const [bio, setBio] = useState(user.bio);
    const [quote, setQuote] = useState(user.quote);
    const [funFact, setFunFact] = useState(user.artistInfo?.funFact || '');
    const [musicId, setMusicId] = useState(user.artistInfo?.musicId || '');
    const [videoId, setVideoId] = useState(user.artistInfo?.videoId || '');
    const [activePaymentModel, setActivePaymentModel] = useState<PaymentModel>(
        user.artistInfo?.paymentModel || PaymentModel.STANDARD,
    );
    const [fileToUpload, setFileToUpload] = useState<File>();
    const [textResponseCost, setTextResponseCost] = useState<number | undefined>(
        user.artistInfo?.costTextResponseType,
    );
    const [videoResponseCost, setVideoResponseCost] = useState<number | undefined>(
        user.artistInfo?.costVideoResponseType,
    );
    const [pwywTextMin, setPwywTextMin] = useState<number | undefined>(
        user.artistInfo?.pwywPrice?.text.min,
    );
    const [pwywTextMax, setPwywTextMax] = useState<number | undefined>(
        user.artistInfo?.pwywPrice?.text.max,
    );
    const [pwywTextSuggested, setPwywTextSuggested] = useState<number | undefined>(
        user.artistInfo?.pwywPrice?.text.suggested,
    );
    const [pwywVideoMin, setPwywVideoMin] = useState<number | undefined>(
        user.artistInfo?.pwywPrice?.video.min,
    );
    const [pwywVideoMax, setPwywVideoMax] = useState<number | undefined>(
        user.artistInfo?.pwywPrice?.video.max,
    );
    const [pwywVideoSuggested, setPwywVideoSuggested] = useState<number | undefined>(
        user.artistInfo?.pwywPrice?.video.suggested,
    );
    const [profilePictureUrl, setProfilePictureUrl] = useState<string>();
    const [notificationSettings, setNotificationSettings] = useState(
        user.userNotificationSettings,
    );

    const [subscriptionIntent, setSubscriptionIntent] = useState<
        SubscriptionIntent | undefined
    >(undefined);

    const [newSubscriptionPrice, setNewSubscriptionPrice] = useState<number>(
        user.artistInfo?.subscriptionSettings?.subscriptionPriceInfo[
            user.artistInfo.subscriptionSettings.activePriceId
        ]?.price || 0,
    );
    const [newSubscriptionLabel, setnewSubscriptionLabel] = useState<string>(
        user.artistInfo?.subscriptionSettings?.productName || 'Premium Subscription',
    );
    const [isSubmitting, setIsSubmitting] = useState(false);

    const artistName = getConfig().artist.name;
    const profileCreated = hasCreatedProfile(user);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const personalInfoRef = useRef<HTMLDivElement>(null);
    const paymentModelRef = useRef<HTMLDivElement>(null);
    const notificationsSettingsRef = useRef<HTMLDivElement>(null);
    const artistSubscriptionSettingsRef = useRef<HTMLDivElement>(null);
    const manageSubscriptionRef = useRef<HTMLDivElement>(null);

    const cache = useCache();

    const scrollToSection = (ref: React.RefObject<HTMLDivElement>) => {
        ref.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const updateUserInCache = (user: User) => {
        const userSummary: UserSummary = {
            username: user.username,
            displayName: user.displayName,
            isArtist: user.isArtist,
            subscriptionStatus: user.subscriptionStatus,
            subscriptionOverride: user.subscriptionOverride,
            quote: user.quote,
        };
        if (cache.userSummaries.get(user.username)) {
            cache.userSummaries.update(userSummary);
        } else {
            cache.userSummaries.put(userSummary);
        }
    };

    const onSave = () => {
        setIsSubmitting(true);

        const newErrors: Record<string, string> = {};

        if (!displayName.trim()) {
            newErrors.displayName = 'This field is required';
        }

        if (subscriptionIntent === 'put' && newSubscriptionPrice <= 0) {
            newErrors.subscriptionPrice = 'Price must be greater than $0';
        }

        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) {
            return;
        }

        const buildArtistInfo = (): ArtistInfo | undefined => {
            return {
                funFact,
                musicId,
                videoId,
                stripeAccountId: user.artistInfo?.stripeAccountId || '',
                onboardingComplete: user.artistInfo?.onboardingComplete ?? false,
                costTextResponseType: textResponseCost ?? 0,
                costVideoResponseType: videoResponseCost ?? 0,
                applicationFee: user.artistInfo?.applicationFee || 0,
                paymentModel: activePaymentModel,
                pwywPrice: {
                    text: {
                        min: pwywTextMin,
                        max: pwywTextMax,
                        suggested: pwywTextSuggested,
                    },
                    video: {
                        min: pwywVideoMin,
                        max: pwywVideoMax,
                        suggested: pwywVideoSuggested,
                    },
                },
                ...(subscriptionIntent && { subscriptionIntent }),
                ...(subscriptionIntent === 'put' && { newSubscriptionPrice }),
                ...(subscriptionIntent === 'put' && { newSubscriptionLabel }),
            };
        };
        const userUpdate: Partial<User> = {
            displayName: displayName.trim(),
            bio,
            hasCreatedProfile: true,
            profilePicAction,
            artistInfo: buildArtistInfo(),
            userNotificationSettings: notificationSettings,
            quote: quote ? quote.trim() : '',
        };

        const handleSuccess = (resp: any) => {
            request.onSuccess('Profile updated');
            setIsSubmitting(false);

            updateUserInCache(resp.data);
            navigate('..');
        };

        const handleError = (err: unknown) => {
            setIsSubmitting(false);

            console.error(err);
            request.onFailure(err);
        };

        request.onStart();

        if (fileToUpload !== undefined) {
            api.userGeneratePresignedUrl(fileToUpload.type)
                .then((resp) =>
                    api.uploadProfilePicToS3({
                        presignedUrl: resp.data.presignedUrl,
                        mediaContent: fileToUpload,
                        contentType: fileToUpload.type,
                    }),
                )
                .then(() => {
                    setProfilePictureUrl('');
                    return api.updateUser(userUpdate);
                })
                .then(handleSuccess)
                .catch((err: unknown) => {
                    console.error('Upload error: ', err);
                    request.onFailure();
                });
        } else {
            api.updateUser(userUpdate).then(handleSuccess).catch(handleError);
        }
    };

    const onChangeProfilePicture = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files ? e.target.files[0] : null;
        if (!file) return;
        if (profilePictureUrl) {
            URL.revokeObjectURL(profilePictureUrl);
            setProfilePictureUrl('');
        }
        setProfilePictureUrl(URL.createObjectURL(file));
        setFileToUpload(file);
        setProfilePicAction(ProfilePicAction.Update);
    };

    const onDeleteProfilePicture = () => {
        setProfilePictureUrl('');
        setFileToUpload(undefined);
        setProfilePicAction(ProfilePicAction.Delete);
    };

    const handlePaymentModelChange = (value: PaymentModel) => {
        setActivePaymentModel(value);
    };

    return (
        <Container maxWidth='lg' sx={{ pt: 6, mb: '100px' }}>
            <Grid container spacing={4}>
                {/* Sidebar */}
                <Grid
                    item
                    xs={12}
                    md={3}
                    sx={{
                        display: { xs: 'none', md: 'block' }, // Hide on small screens
                        borderRight: { md: '1px solid #ddd' },
                        pr: 2,
                    }}
                >
                    <List>
                        <ListItem disablePadding>
                            <ListItemButton
                                onClick={() => scrollToSection(personalInfoRef)}
                            >
                                <ListItemIcon sx={{ minWidth: '35px' }}>
                                    <Person sx={{ color: theme.palette.primary.main }} />
                                </ListItemIcon>
                                <ListItemText>
                                    <Typography sx={{ fontFamily: 'LemonMilk' }}>
                                        Personal Info
                                    </Typography>
                                </ListItemText>
                            </ListItemButton>
                        </ListItem>
                        {user.isArtist && (
                            <>
                                <ListItem disablePadding>
                                    <ListItemButton
                                        onClick={() => scrollToSection(paymentModelRef)}
                                    >
                                        <ListItemIcon sx={{ minWidth: '35px' }}>
                                            <QuestionAnswer
                                                sx={{ color: theme.palette.primary.main }}
                                            />
                                        </ListItemIcon>
                                        <ListItemText>
                                            <Typography sx={{ fontFamily: 'LemonMilk' }}>
                                                Requests Payment Model
                                            </Typography>
                                        </ListItemText>
                                    </ListItemButton>
                                </ListItem>
                                <ListItem disablePadding>
                                    <ListItemButton
                                        onClick={() =>
                                            scrollToSection(artistSubscriptionSettingsRef)
                                        }
                                    >
                                        <ListItemIcon sx={{ minWidth: '35px' }}>
                                            <CurrencyExchange
                                                sx={{ color: theme.palette.primary.main }}
                                            />
                                        </ListItemIcon>
                                        <ListItemText>
                                            <Typography sx={{ fontFamily: 'LemonMilk' }}>
                                                Subscription Settings
                                            </Typography>
                                        </ListItemText>
                                    </ListItemButton>
                                </ListItem>
                            </>
                        )}
                        <ListItem disablePadding>
                            <ListItemButton
                                onClick={() => scrollToSection(notificationsSettingsRef)}
                            >
                                <ListItemIcon sx={{ minWidth: '35px' }}>
                                    <NotificationsActive
                                        sx={{ color: theme.palette.primary.main }}
                                    />
                                </ListItemIcon>
                                <ListItemText>
                                    <Typography sx={{ fontFamily: 'LemonMilk' }}>
                                        Notifications
                                    </Typography>
                                </ListItemText>
                            </ListItemButton>
                        </ListItem>
                    </List>
                </Grid>

                <Grid item xs={12} md={9}>
                    {!profileCreated && (
                        <Alert severity='warning' sx={{ mb: 2 }}>
                            Finish profile before interacting with {artistName}.
                        </Alert>
                    )}

                    <Stack spacing={5}>
                        <Box
                            sx={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 10,
                                backgroundColor: 'background.paper',
                                py: 2,
                                borderBottom: '1px solid #eee',
                            }}
                        >
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                justifyContent='space-between'
                                alignItems='center'
                                spacing={2}
                            >
                                <Typography
                                    variant='h4'
                                    textAlign={{ xs: 'center', sm: 'left' }}
                                >
                                    Edit Profile
                                </Typography>
                                <Stack direction='row' spacing={2}>
                                    <LoadingButton
                                        variant='contained'
                                        onClick={onSave}
                                        loading={request.isLoading()}
                                        startIcon={<SaveIcon />}
                                    >
                                        Save
                                    </LoadingButton>
                                    <Button
                                        variant='contained'
                                        color='secondary'
                                        onClick={() => navigate('..')}
                                        startIcon={<NotInterestedIcon />}
                                    >
                                        Cancel
                                    </Button>
                                </Stack>
                            </Stack>
                        </Box>
                        {Object.values(errors).length > 0 && (
                            <Alert severity='error' sx={{ mb: 3 }} variant='filled'>
                                Unable to save profile. Fix the errors below and retry.
                            </Alert>
                        )}

                        {/* Personal Information Section */}
                        <Stack ref={personalInfoRef}>
                            <Typography variant='h5' gutterBottom>
                                <Person
                                    style={{
                                        verticalAlign: 'middle',
                                        marginRight: '0.1em',
                                    }}
                                />{' '}
                                Personal Info
                            </Typography>
                            <Stack spacing={4}>
                                <Stack direction='row' alignItems='center' spacing={3}>
                                    <Avatar
                                        user={user}
                                        size={150}
                                        url={profilePictureUrl}
                                    />
                                    <Stack spacing={2} alignItems='start'>
                                        <Button
                                            component='label'
                                            variant='outlined'
                                            startIcon={<PhotoLibraryIcon />}
                                        >
                                            Upload
                                            <input
                                                type='file'
                                                accept='image/*'
                                                hidden
                                                onChange={onChangeProfilePicture}
                                            />
                                        </Button>
                                        <Button
                                            component='label'
                                            variant='outlined'
                                            onClick={onDeleteProfilePicture}
                                            startIcon={<DeleteIcon />}
                                        >
                                            Delete
                                        </Button>
                                    </Stack>
                                </Stack>
                                <TextField
                                    required
                                    label='Display Name'
                                    value={displayName}
                                    onChange={(e) => setDisplayName(e.target.value)}
                                    error={!!errors.displayName}
                                    helperText={
                                        errors.displayName ||
                                        'This is how other users will identify you'
                                    }
                                    fullWidth
                                />
                                <TextField
                                    label='Bio'
                                    multiline
                                    minRows={3}
                                    maxRows={6}
                                    value={bio}
                                    onChange={(e) => setBio(e.target.value)}
                                    helperText='Tell us about yourself!'
                                    fullWidth
                                />
                                <TextField
                                    label='Quote'
                                    multiline
                                    minRows={1}
                                    maxRows={4}
                                    value={quote}
                                    onChange={(e) => setQuote(e.target.value)}
                                    helperText={`A quote from ${artistName} that will be displayed on your profile.`}
                                    fullWidth
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position='start'>
                                                <Typography color='secondary'>
                                                    "
                                                </Typography>
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position='end'>
                                                <Typography color='secondary'>
                                                    "
                                                </Typography>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                {user.isArtist && (
                                    <>
                                        <TextField
                                            label='Fun Fact'
                                            multiline
                                            minRows={3}
                                            maxRows={6}
                                            value={funFact}
                                            onChange={(e) => setFunFact(e.target.value)}
                                            fullWidth
                                        />
                                        <TextField
                                            label='Music ID'
                                            maxRows={1}
                                            value={musicId}
                                            onChange={(event) =>
                                                setMusicId(event.target.value)
                                            }
                                        />
                                        <TextField
                                            label='Video ID'
                                            maxRows={1}
                                            value={videoId}
                                            onChange={(event) =>
                                                setVideoId(event.target.value)
                                            }
                                        />
                                    </>
                                )}
                            </Stack>
                        </Stack>
                        {/* Artist Only: Payment Model Section */}
                        {user.isArtist && (
                            <>
                                <Stack ref={paymentModelRef}>
                                    <Typography variant='h5' gutterBottom>
                                        <QuestionAnswer
                                            style={{
                                                verticalAlign: 'middle',
                                                marginRight: '0.1em',
                                            }}
                                        />{' '}
                                        Requests Payment Model
                                    </Typography>
                                    <Stack spacing={4}>
                                        <Box>
                                            {/* Standard Pricing */}
                                            <Box mb={3}>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={
                                                                activePaymentModel ===
                                                                PaymentModel.STANDARD
                                                            }
                                                            onChange={() =>
                                                                handlePaymentModelChange(
                                                                    PaymentModel.STANDARD,
                                                                )
                                                            }
                                                        />
                                                    }
                                                    label='Standard Pricing'
                                                    sx={{
                                                        '& .MuiFormControlLabel-label': {
                                                            fontFamily: 'LemonMilk', // Apply LemonMilk font to the label
                                                            fontSize: '1.2rem', // Adjust font size as needed
                                                        },
                                                    }}
                                                />
                                                <Stack
                                                    direction='row'
                                                    alignItems='center'
                                                    spacing={2}
                                                    pl={4}
                                                >
                                                    <Stack
                                                        spacing={2}
                                                        alignItems='center'
                                                    >
                                                        <Typography
                                                            variant='h6'
                                                            sx={{
                                                                fontFamily: 'LemonMilk',
                                                            }}
                                                        >
                                                            <Icon
                                                                name={ResponseType.Text}
                                                                color='primary'
                                                                sx={{
                                                                    marginRight: '0.4rem',
                                                                    verticalAlign:
                                                                        'middle',
                                                                }}
                                                            />
                                                            Text
                                                        </Typography>
                                                        <Divider
                                                            sx={{
                                                                height: '4px',
                                                                backgroundColor:
                                                                    theme.palette.primary
                                                                        .main,
                                                                width: '100%',
                                                            }}
                                                        />
                                                        <TextField
                                                            label='Text'
                                                            type='number'
                                                            value={textResponseCost}
                                                            onChange={(e) =>
                                                                setTextResponseCost(
                                                                    +e.target.value,
                                                                )
                                                            }
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position='start'>
                                                                        <Typography color='secondary'>
                                                                            $
                                                                        </Typography>
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                            sx={{
                                                                width: '100px',
                                                            }}
                                                        />
                                                    </Stack>

                                                    <Stack
                                                        spacing={2}
                                                        alignItems='center'
                                                    >
                                                        <Typography
                                                            variant='h6'
                                                            sx={{
                                                                fontFamily: 'LemonMilk',
                                                            }}
                                                        >
                                                            <Icon
                                                                name={ResponseType.Video}
                                                                color='primary'
                                                                sx={{
                                                                    marginRight: '0.4rem',
                                                                    verticalAlign:
                                                                        'middle',
                                                                }}
                                                            />
                                                            Video
                                                        </Typography>
                                                        <Divider
                                                            sx={{
                                                                height: '4px',
                                                                backgroundColor:
                                                                    theme.palette.primary
                                                                        .main,
                                                                width: '100%',
                                                            }}
                                                        />
                                                        <TextField
                                                            label='Video'
                                                            type='number'
                                                            value={videoResponseCost}
                                                            onChange={(e) =>
                                                                setVideoResponseCost(
                                                                    +e.target.value,
                                                                )
                                                            }
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position='start'>
                                                                        <Typography color='secondary'>
                                                                            $
                                                                        </Typography>
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                            sx={{
                                                                width: '100px',
                                                            }}
                                                        />
                                                    </Stack>
                                                </Stack>
                                            </Box>
                                            <Box>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={
                                                                activePaymentModel ===
                                                                PaymentModel.PWYW
                                                            }
                                                            onChange={() =>
                                                                handlePaymentModelChange(
                                                                    PaymentModel.PWYW,
                                                                )
                                                            }
                                                        />
                                                    }
                                                    label='Pay What You Want'
                                                    sx={{
                                                        '& .MuiFormControlLabel-label': {
                                                            fontFamily: 'LemonMilk', // Apply LemonMilk font to the label
                                                            fontSize: '1.2rem', // Adjust font size as needed
                                                        },
                                                    }}
                                                />
                                                <Stack
                                                    direction={{
                                                        md: 'column',
                                                        lg: 'row',
                                                    }}
                                                    spacing={4}
                                                >
                                                    <Stack
                                                        spacing={2}
                                                        alignItems='center'
                                                    >
                                                        <Typography
                                                            variant='h6'
                                                            sx={{
                                                                fontFamily: 'LemonMilk',
                                                            }}
                                                        >
                                                            <Icon
                                                                name={ResponseType.Text}
                                                                color='primary'
                                                                sx={{
                                                                    marginRight: '0.4rem',
                                                                    verticalAlign:
                                                                        'middle',
                                                                }}
                                                            />
                                                            Text
                                                        </Typography>
                                                        <Divider
                                                            sx={{
                                                                height: '4px',
                                                                backgroundColor:
                                                                    theme.palette.primary
                                                                        .main,
                                                                width: '100%',
                                                            }}
                                                        />
                                                        <Stack
                                                            spacing={2}
                                                            direction='row'
                                                            alignItems='center'
                                                        >
                                                            <TextField
                                                                label='Minimum'
                                                                type='number'
                                                                value={pwywTextMin}
                                                                onChange={(e) =>
                                                                    setPwywTextMin(
                                                                        +e.target.value,
                                                                    )
                                                                }
                                                                InputProps={{
                                                                    startAdornment: (
                                                                        <InputAdornment position='start'>
                                                                            <Typography color='secondary'>
                                                                                $
                                                                            </Typography>
                                                                        </InputAdornment>
                                                                    ),
                                                                }}
                                                                sx={{
                                                                    width: '100px',
                                                                }}
                                                            />
                                                            <TextField
                                                                label='Suggested'
                                                                type='number'
                                                                value={pwywTextSuggested}
                                                                onChange={(e) =>
                                                                    setPwywTextSuggested(
                                                                        +e.target.value,
                                                                    )
                                                                }
                                                                InputProps={{
                                                                    startAdornment: (
                                                                        <InputAdornment position='start'>
                                                                            <Typography color='secondary'>
                                                                                $
                                                                            </Typography>
                                                                        </InputAdornment>
                                                                    ),
                                                                }}
                                                                sx={{
                                                                    width: '100px',
                                                                }}
                                                            />
                                                            <TextField
                                                                label='Maximum'
                                                                type='number'
                                                                value={pwywTextMax}
                                                                onChange={(e) =>
                                                                    setPwywTextMax(
                                                                        +e.target.value,
                                                                    )
                                                                }
                                                                InputProps={{
                                                                    startAdornment: (
                                                                        <InputAdornment position='start'>
                                                                            <Typography color='secondary'>
                                                                                $
                                                                            </Typography>
                                                                        </InputAdornment>
                                                                    ),
                                                                }}
                                                                sx={{
                                                                    width: '100px',
                                                                }}
                                                            />
                                                        </Stack>
                                                    </Stack>

                                                    <Stack
                                                        spacing={2}
                                                        alignItems='center'
                                                    >
                                                        <Typography
                                                            variant='h6'
                                                            sx={{
                                                                fontFamily: 'LemonMilk',
                                                            }}
                                                        >
                                                            <Icon
                                                                name={ResponseType.Video}
                                                                color='primary'
                                                                sx={{
                                                                    marginRight: '0.4rem',
                                                                    verticalAlign:
                                                                        'middle',
                                                                }}
                                                            />
                                                            Video
                                                        </Typography>
                                                        <Divider
                                                            sx={{
                                                                height: '4px',
                                                                backgroundColor:
                                                                    theme.palette.primary
                                                                        .main,
                                                                width: '100%',
                                                            }}
                                                        />
                                                        <Stack
                                                            spacing={2}
                                                            direction='row'
                                                            alignItems='center'
                                                        >
                                                            <TextField
                                                                label='Minimum'
                                                                type='number'
                                                                value={pwywVideoMin}
                                                                onChange={(e) =>
                                                                    setPwywVideoMin(
                                                                        +e.target.value,
                                                                    )
                                                                }
                                                                InputProps={{
                                                                    startAdornment: (
                                                                        <InputAdornment position='start'>
                                                                            <Typography color='secondary'>
                                                                                $
                                                                            </Typography>
                                                                        </InputAdornment>
                                                                    ),
                                                                }}
                                                                sx={{
                                                                    width: '100px',
                                                                }}
                                                            />
                                                            <TextField
                                                                label='Suggested'
                                                                type='number'
                                                                value={pwywVideoSuggested}
                                                                onChange={(e) =>
                                                                    setPwywVideoSuggested(
                                                                        +e.target.value,
                                                                    )
                                                                }
                                                                InputProps={{
                                                                    startAdornment: (
                                                                        <InputAdornment position='start'>
                                                                            <Typography color='secondary'>
                                                                                $
                                                                            </Typography>
                                                                        </InputAdornment>
                                                                    ),
                                                                }}
                                                                sx={{
                                                                    width: '100px',
                                                                }}
                                                            />
                                                            <TextField
                                                                label='Maximum'
                                                                type='number'
                                                                value={pwywVideoMax}
                                                                onChange={(e) =>
                                                                    setPwywVideoMax(
                                                                        +e.target.value,
                                                                    )
                                                                }
                                                                InputProps={{
                                                                    startAdornment: (
                                                                        <InputAdornment position='start'>
                                                                            <Typography color='secondary'>
                                                                                $
                                                                            </Typography>
                                                                        </InputAdornment>
                                                                    ),
                                                                }}
                                                                sx={{
                                                                    width: '100px',
                                                                }}
                                                            />
                                                        </Stack>
                                                    </Stack>
                                                </Stack>
                                            </Box>
                                        </Box>
                                    </Stack>
                                </Stack>
                                {/* Artist Only: Subscription Settings Section */}
                                <Stack ref={artistSubscriptionSettingsRef}>
                                    <ArtistSubscriptionSettingsEditor
                                        user={user}
                                        subscriptionSettings={
                                            user.artistInfo?.subscriptionSettings
                                        }
                                        subscriptionIntent={subscriptionIntent}
                                        setSubscriptionIntent={setSubscriptionIntent}
                                        newSubscriptionPrice={newSubscriptionPrice}
                                        setNewSubscriptionPrice={setNewSubscriptionPrice}
                                        newSubscriptionLabel={newSubscriptionLabel}
                                        setnewSubscriptionLabel={setnewSubscriptionLabel}
                                        errors={errors}
                                        disabled={isSubmitting}
                                    />
                                </Stack>
                            </>
                        )}
                        {/* Notification Settings Section */}
                        <Stack ref={notificationsSettingsRef}>
                            <NotificationSettingsEditor
                                user={user}
                                notificationSettings={notificationSettings}
                                setNotificationSettings={setNotificationSettings}
                            />
                        </Stack>
                        {/* Manage Subscription Section */}
                        <Stack ref={manageSubscriptionRef}>
                            <SubscriptionManager user={user} />
                        </Stack>

                        <RequestSnackbar request={request} />
                    </Stack>
                </Grid>
            </Grid>
        </Container>
    );
};

export default ProfileEditorPage;
